import {testIdProps} from '@github-ui/test-id-props'
import {Button} from '@primer/react'
import {clsx} from 'clsx'
import {memo, type RefObject, startTransition, useCallback, useState} from 'react'

import {MergeabilitySidesheet} from './MergeabilitySidesheet'
import {presentationForStatus, Status, mergeabilityStatus} from '../helpers/mergeability-status'
import {useMergeBoxPageData} from '../page-data/loaders/use-merge-box-page-data'
import {useMergeMethodContext} from '../contexts/MergeMethodContext'
import type {MergeBoxPageData} from '../page-data/payloads/merge-box'
import {useMergeabilityLiveUpdates, type Channels} from '../hooks/use-mergeability-live-updates'
import {useStatusChecksPageDataWithoutError} from '../page-data/loaders/use-status-checks-page-data'
import type {StatusChecksPageData} from '../page-data/payloads/status-checks'
import styles from './MergeStatusButton.module.css'
import {useAnalytics} from '@github-ui/use-analytics'

interface MergeStatusButtonCommonProps {
  mergeStatusButtonRef: RefObject<HTMLButtonElement | null>
  toggleMergeabilitySidesheet: (isOpen: boolean) => void
}

/**
 * A button that displays the merge status of a pull request.
 * Opens the mergeability sidesheet.
 */
export const MergeStatusButtonWrapper = memo(function MergeStatusButtonWrapper({
  channels,
  headSha,
  helpUrl,
  viewerLogin,
  isDockedMode,
  onToggleDockedPanel,
  mergeStatusButtonRef,
  size,
  variant,
}: {
  channels: Channels
  headSha: string
  helpUrl: string
  viewerLogin: string
  /** When true, suppresses the MergeabilitySidesheet and delegates click to onToggleDockedPanel */
  isDockedMode: boolean
  /** Called on button click when isDockedMode is true */
  onToggleDockedPanel: () => void
  mergeStatusButtonRef: RefObject<HTMLButtonElement | null>
  size?: 'small' | 'medium'
  variant?: 'default' | 'invisible'
}) {
  const {mergeMethod} = useMergeMethodContext()
  const {data, refetch} = useMergeBoxPageData({mergeMethod, bypassRequirements: false})
  const {data: statusChecksData} = useStatusChecksPageDataWithoutError({
    pullRequestHeadSha: headSha,
  })

  const [mergeabilitySidesheetIsOpen, setMergeabilitySidesheetIsOpen] = useState(false)
  const toggleMergeabilitySidesheet = useCallback(
    (isOpen: boolean) => {
      if (isOpen && isDockedMode) {
        onToggleDockedPanel()
        return
      }
      setMergeabilitySidesheetIsOpen(isOpen)
    },
    [isDockedMode, onToggleDockedPanel],
  )

  const refetchQuery = useCallback(() => {
    startTransition(() => {
      refetch({cancelRefetch: false})
    })
  }, [refetch])

  useMergeabilityLiveUpdates({refetchQuery, channels})

  const isLoading = !data || !statusChecksData

  return (
    <>
      <MergeStatusButton
        isLoading={isLoading}
        pullRequest={data?.pullRequest}
        mergeRequirements={data?.mergeRequirements}
        statusChecksData={statusChecksData}
        mergeStatusButtonRef={mergeStatusButtonRef}
        toggleMergeabilitySidesheet={toggleMergeabilitySidesheet}
        size={size}
        variant={variant}
      />
      {!isDockedMode && (
        <MergeabilitySidesheet
          helpUrl={helpUrl}
          mergeStatusButtonRef={mergeStatusButtonRef}
          mergeabilitySidesheetIsOpen={mergeabilitySidesheetIsOpen}
          toggleMergeabilitySidesheet={toggleMergeabilitySidesheet}
          viewerLogin={viewerLogin}
          channels={channels}
        />
      )}
    </>
  )
})

export type MergeStatusButtonData = MergeBoxPageData

interface MergeStatusButtonProps extends MergeStatusButtonCommonProps {
  isLoading?: boolean
  pullRequest?: MergeBoxPageData['pullRequest']
  mergeRequirements?: MergeBoxPageData['mergeRequirements']
  statusChecksData?: StatusChecksPageData
  size?: 'small' | 'medium'
  variant?: 'default' | 'invisible'
}

export function MergeStatusButton({
  isLoading,
  pullRequest,
  mergeRequirements,
  statusChecksData,
  mergeStatusButtonRef,
  toggleMergeabilitySidesheet,
  size = 'medium',
  variant = 'default',
}: MergeStatusButtonProps) {
  const {sendAnalyticsEvent} = useAnalytics()

  if (!mergeStatusButtonRef) return null

  let status = null

  if (!isLoading && pullRequest) {
    status = mergeabilityStatus({pullRequest, mergeRequirements: mergeRequirements ?? null, statusChecksData})
  }

  const defaultPresentation = {
    icon: undefined,
    iconColor: undefined,
    iconColorClass: undefined,
    title: 'View status',
  }

  const presentation =
    !status || status === Status.NonactionableFailure || status === Status.Unknown
      ? defaultPresentation
      : presentationForStatus(status)

  return (
    <Button
      ref={mergeStatusButtonRef}
      leadingVisual={presentation?.icon}
      variant={variant}
      {...testIdProps('merge-status-button')}
      className={clsx(styles.mergeStatusButton, presentation?.iconColorClass && styles[presentation.iconColorClass])}
      onClick={() => {
        if (toggleMergeabilitySidesheet) {
          toggleMergeabilitySidesheet(true)
          sendAnalyticsEvent('merge_status_side_panel.open', 'MERGE_STATUS_SIDE_PANEL_BUTTON')
        }
      }}
      size={size}
      loading={isLoading || status === Status.Unknown}
      loadingAnnouncement="Loading merge status"
      inactive={isLoading || status === Status.Unknown}
    >
      <span className="d-flex hide-lg hide-xl">View status</span>
      <span className="hide-sm hide-md d-lg-flex">{presentation?.title}</span>
    </Button>
  )
}
